/**
 * Гроші (FLD-05; docs/architecture/money-type.md): значення `{ amount, currency }`, де сума —
 * десятковий рядок (жодних float), а валюта — код ISO 4217 з довідника нижче. Одна нормалізація
 * для запису DAL, умов мови фільтрів, імпорту й перенесення. Модуль без zod і без імпортів: його
 * беруть оцінювач умов у браузері (`./filter-eval`) і форма поля.
 *
 * Режим до вікна — «валюта на значення» (розд. 2): кожна сума має свій код. Які валюти активні й
 * яка за замовчуванням — налаштування компанії `money.currencies` / `money.defaultCurrency`
 * (розд. 4), дозволені валюти поля — параметр `currencies`. Різні валюти мовчки не додаються ніде.
 */

/** Значення грошового поля в API, аудиті й DAL: сума рядком і код валюти. */
export interface MoneyValue {
  amount: string;
  currency: string;
}

/**
 * Довідник валют ISO 4217 — код → кількість дробових одиниць (таблиця A.1, чинні коди на 2026).
 * Сталий у коді: компанія валют не створює, лише вмикає (ADM-803). Фонди, метали й тестові коди
 * (`XAU`, `XDR`, `XTS`, `XXX`…) не входять: грошових сум у них не ведуть. Вилучені з обігу коди
 * (`HRK`, `BGN`, `ANG`, `SLL`…) теж не входять — нове значення в них не прийме ніхто, а збережене
 * читається як є (довідник перевіряє лише нове значення).
 */
export const CURRENCY_MINOR_UNITS: Readonly<Record<string, number>> = Object.freeze({
  AED: 2,
  AFN: 2,
  ALL: 2,
  AMD: 2,
  AOA: 2,
  ARS: 2,
  AUD: 2,
  AWG: 2,
  AZN: 2,
  BAM: 2,
  BBD: 2,
  BDT: 2,
  BHD: 3,
  BIF: 0,
  BMD: 2,
  BND: 2,
  BOB: 2,
  BRL: 2,
  BSD: 2,
  BTN: 2,
  BWP: 2,
  BYN: 2,
  BZD: 2,
  CAD: 2,
  CDF: 2,
  CHF: 2,
  CLP: 0,
  CNY: 2,
  COP: 2,
  CRC: 2,
  CUP: 2,
  CVE: 2,
  CZK: 2,
  DJF: 0,
  DKK: 2,
  DOP: 2,
  DZD: 2,
  EGP: 2,
  ERN: 2,
  ETB: 2,
  EUR: 2,
  FJD: 2,
  FKP: 2,
  GBP: 2,
  GEL: 2,
  GHS: 2,
  GIP: 2,
  GMD: 2,
  GNF: 0,
  GTQ: 2,
  GYD: 2,
  HKD: 2,
  HNL: 2,
  HTG: 2,
  HUF: 2,
  IDR: 2,
  ILS: 2,
  INR: 2,
  IQD: 3,
  IRR: 2,
  ISK: 0,
  JMD: 2,
  JOD: 3,
  JPY: 0,
  KES: 2,
  KGS: 2,
  KHR: 2,
  KMF: 0,
  KPW: 2,
  KRW: 0,
  KWD: 3,
  KYD: 2,
  KZT: 2,
  LAK: 2,
  LBP: 2,
  LKR: 2,
  LRD: 2,
  LSL: 2,
  LYD: 3,
  MAD: 2,
  MDL: 2,
  MGA: 2,
  MKD: 2,
  MMK: 2,
  MNT: 2,
  MOP: 2,
  MRU: 2,
  MUR: 2,
  MVR: 2,
  MWK: 2,
  MXN: 2,
  MYR: 2,
  MZN: 2,
  NAD: 2,
  NGN: 2,
  NIO: 2,
  NOK: 2,
  NPR: 2,
  NZD: 2,
  OMR: 3,
  PAB: 2,
  PEN: 2,
  PGK: 2,
  PHP: 2,
  PKR: 2,
  PLN: 2,
  PYG: 0,
  QAR: 2,
  RON: 2,
  RSD: 2,
  RUB: 2,
  RWF: 0,
  SAR: 2,
  SBD: 2,
  SCR: 2,
  SDG: 2,
  SEK: 2,
  SGD: 2,
  SHP: 2,
  SLE: 2,
  SOS: 2,
  SRD: 2,
  SSP: 2,
  STN: 2,
  SVC: 2,
  SYP: 2,
  SZL: 2,
  THB: 2,
  TJS: 2,
  TMT: 2,
  TND: 3,
  TOP: 2,
  TRY: 2,
  TTD: 2,
  TWD: 2,
  TZS: 2,
  UAH: 2,
  UGX: 0,
  USD: 2,
  UYU: 2,
  UZS: 2,
  VED: 2,
  VES: 2,
  VND: 0,
  VUV: 0,
  WST: 2,
  XAF: 0,
  XCD: 2,
  XCG: 2,
  XOF: 0,
  XPF: 0,
  YER: 2,
  ZAR: 2,
  ZMW: 2,
  ZWG: 2,
});

/** Форма коду валюти: три латинські великі літери (той самий шаблон — CHECK колонки `__currency`). */
export const CURRENCY_CODE = /^[A-Z]{3}$/;

export const MONEY_LIMITS = {
  /**
   * Значущих цифр суми: як у числа з точністю (`normalizeValue`, ≤ 15 − `scale` цілих), бо
   * клієнти й експорт можуть читати суму числом. UAH — 13 цілих цифр.
   */
  digits: 15,
  /** Валют у параметрі поля `currencies` і в налаштуванні `money.currencies`. */
  currencies: 20,
} as const;

/** Валюта з довідника (нове значення): `false` — невідомий чи вилучений код. */
export function isKnownCurrency(code: unknown): code is string {
  return (
    typeof code === 'string' &&
    CURRENCY_CODE.test(code) &&
    Object.hasOwn(CURRENCY_MINOR_UNITS, code)
  );
}

/** Дробових одиниць валюти; невідомий код — 2 (лише для показу збереженого, не для перевірки). */
export function minorUnitsOf(code: string): number {
  return Object.hasOwn(CURRENCY_MINOR_UNITS, code) ? (CURRENCY_MINOR_UNITS[code] as number) : 2;
}

/**
 * Коди відмови значення (без значень у тексті, SEC-110): `invalid_money` — сума числом JSON (float
 * губить копійки великих сум) чи не десятковий рядок, `money_scale` — більше знаків, ніж дробових
 * одиниць валюти (DAL не округлює мовчки), `range` — понад межу цифр чи межі поля, `currency_invalid`
 * — код поза довідником, `type` — значення не об'єкт `{ amount, currency }`.
 */
export type MoneyErrorCode =
  | 'type'
  | 'invalid_money'
  | 'money_scale'
  | 'range'
  | 'currency_invalid';

export type NormalizedMoney =
  | { ok: true; value: MoneyValue | null }
  | { ok: false; code: MoneyErrorCode };

/** Десятковий рядок суми: без експоненти, пробілів і `+`; до 20 знаків дробу до перевірки точності. */
const AMOUNT = /^-?\d{1,20}(\.\d{1,20})?$/;

/** Канонічна сума: без нулів на початку й у кінці дробу, без `-0`. */
export function canonAmount(text: string): string {
  let out = text;
  if (out.includes('.')) out = out.replace(/0+$/, '').replace(/\.$/, '');
  out = out.replace(/^(-?)0+(?=\d)/, '$1');
  return out === '-0' ? '0' : out;
}

export interface MoneyBounds {
  /** Межі суми поля (параметр `min` / `max`, як у числа); `null` — без межі. */
  min?: number | null;
  max?: number | null;
}

/**
 * Нормалізація значення-грошей (money-type.md, розд. 2): `null` / `undefined` — порожньо; сума —
 * рядок, канонічна форма; валюта — код довідника. Точність — за валютою: `100.50` UAH — так,
 * `100.505` UAH — `money_scale`, `100.0` JPY — так (нулі дробу не рахуються). Цілих цифр —
 * ≤ 15 − дробові одиниці. Чи дозволена валюта полю й компанії, перевіряє DAL (йому потрібні
 * налаштування й поточне значення): тут — лише форма й довідник.
 */
export function normalizeMoney(raw: unknown, bounds: MoneyBounds = {}): NormalizedMoney {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, code: 'type' };
  const input = raw as Record<string, unknown>;
  const keys = Object.keys(input);
  if (keys.length !== 2 || !Object.hasOwn(input, 'amount') || !Object.hasOwn(input, 'currency')) {
    return { ok: false, code: 'type' };
  }
  const { amount, currency } = input;
  if (typeof amount === 'number') return { ok: false, code: 'invalid_money' };
  if (typeof amount !== 'string' || !AMOUNT.test(amount)) {
    return { ok: false, code: 'invalid_money' };
  }
  if (!isKnownCurrency(currency)) return { ok: false, code: 'currency_invalid' };
  return checkAmount(canonAmount(amount), currency, bounds);
}

/** Точність, межа цифр і межі поля для канонічної суми у відомій валюті. */
function checkAmount(amount: string, currency: string, bounds: MoneyBounds): NormalizedMoney {
  const scale = minorUnitsOf(currency);
  const [whole = '', fraction = ''] = amount.replace(/^-/, '').split('.');
  if (fraction.length > scale) return { ok: false, code: 'money_scale' };
  if (whole.length > MONEY_LIMITS.digits - scale) return { ok: false, code: 'range' };
  const n = Number(amount);
  if (
    (bounds.min !== undefined && bounds.min !== null && n < bounds.min) ||
    (bounds.max !== undefined && bounds.max !== null && n > bounds.max)
  ) {
    return { ok: false, code: 'range' };
  }
  return { ok: true, value: { amount, currency } };
}

/**
 * Збережене значення (колонки БД, аудит, база запиту) у канонічній формі — без перевірок довідника,
 * точності й меж: адміністратор міг звузити їх пізніше, а валюту — вимкнути. Непридатне — `null`.
 */
export function storedMoney(raw: unknown): MoneyValue | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { amount, currency } = raw as Record<string, unknown>;
  const text = typeof amount === 'number' && Number.isFinite(amount) ? String(amount) : amount;
  if (typeof text !== 'string' || !AMOUNT.test(text)) return null;
  if (typeof currency !== 'string' || !CURRENCY_CODE.test(currency)) return null;
  return { amount: canonAmount(text), currency };
}

/** Рівність двох значень-грошей (порожнє — `null`). */
export function sameMoney(a: MoneyValue | null, b: MoneyValue | null): boolean {
  if (a === null || b === null) return a === b;
  return a.amount === b.amount && a.currency === b.currency;
}

// ---------------------------------------------------------------- умови (filter-language.md, розд. 2)

/**
 * Значення-гроші в умові мови фільтрів: об'єкт `{ amount, currency }` з відомою валютою. Точність
 * валюти тут не перевіряється (умова `> 100.505 UAH` має сенс); сума — до 15 цифр і 6 знаків дробу,
 * як число в умові (`filterLiteral`). Непридатне — `null`.
 */
export function moneyLiteral(value: unknown): MoneyValue | null {
  if (!isMoneyObject(value)) return null;
  const { amount, currency } = value;
  if (typeof amount !== 'string' || !/^-?\d{1,15}(\.\d{1,6})?$/.test(amount)) return null;
  if (!isKnownCurrency(currency)) return null;
  return { amount: canonAmount(amount), currency };
}

/** Об'єкт із рівно двома ключами `amount` і `currency` (не токен, не масив). */
export function isMoneyObject(value: unknown): value is { amount: unknown; currency: unknown } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length === 2 && keys.includes('amount') && keys.includes('currency');
}

// ---------------------------------------------------------------- імпорт і експорт (розд. 3)

/** Клітинки експорту: сума й валюта двома колонками; порожнє — дві порожні клітинки. */
export function moneyExportCells(value: MoneyValue | null): [string, string] {
  return value === null ? ['', ''] : [value.amount, value.currency];
}

export type MoneyImportResult =
  | { ok: true; value: MoneyValue | null }
  | { ok: false; code: MoneyErrorCode | 'currency_required' };

/**
 * Дві колонки імпорту → значення (money-type.md, розд. 3). Сума — десятковий рядок з крапкою чи
 * комою, пробіли розрядів прибираються. Колонка валюти порожня чи не зіставлена — валюта за
 * замовчуванням поля, лише коли полю дозволена одна валюта (`fixed`); інакше `currency_required`
 * (помилка зіставлення: суму без валюти не вгадуємо). Обидві порожні — порожнє значення.
 */
export function moneyFromImportCells(
  amount: string | null | undefined,
  currency: string | null | undefined,
  options: { fixed: string | null; bounds?: MoneyBounds },
): MoneyImportResult {
  const rawAmount = (amount ?? '').replace(/[\s  ]/g, '').replace(',', '.');
  const rawCurrency = (currency ?? '').trim().toUpperCase();
  if (rawAmount === '' && rawCurrency === '') return { ok: true, value: null };
  if (rawAmount === '') return { ok: false, code: 'invalid_money' };
  const code = rawCurrency === '' ? options.fixed : rawCurrency;
  if (code === null) return { ok: false, code: 'currency_required' };
  return normalizeMoney({ amount: rawAmount, currency: code }, options.bounds);
}
